import { useState, useEffect } from 'react';
import { qdfAPI, monthlyReportAPI } from '../../../services/api';
import { monthValueToParts } from '../utils/dateHelpers';

export const useMonthlySummary = () => {
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [monthlySummary, setMonthlySummary] = useState(null);
  const [monthlySummaryError, setMonthlySummaryError] = useState('');
  const [monthlySummaryLoading, setMonthlySummaryLoading] = useState(false);
  const [generatedReports, setGeneratedReports] = useState([]);
  const [generatedReportsLoading, setGeneratedReportsLoading] = useState(false);
  const [isMonthlySummaryModalOpen, setIsMonthlySummaryModalOpen] = useState(false);
  const [pmGenerating, setPmGenerating] = useState(false);
  const [pmGenerationResult, setPmGenerationResult] = useState(null);

  const loadMonthlySummary = async (monthValue) => {
    try {
      setMonthlySummaryLoading(true);
      setMonthlySummaryError('');
      const { month, year } = monthValueToParts(monthValue);
      const response = await qdfAPI.getMonthlySummary({ month, year });
      setMonthlySummary(response.data.summary);
    } catch (err) {
      console.error('Failed to load monthly summary:', err);
      setMonthlySummary(null);
      const backendMessage = err.response?.data?.message || '';
      if (backendMessage === 'Error fetching QDF report') {
        setMonthlySummaryError('Monthly summary endpoint is not available on the running backend. Redeploy backend to latest version and retry.');
      } else {
        setMonthlySummaryError(backendMessage || 'Failed to load monthly summary.');
      }
    } finally {
      setMonthlySummaryLoading(false);
    }
  };

  const loadGeneratedReports = async (monthValue) => {
    try {
      setGeneratedReportsLoading(true);
      const { month, year } = monthValueToParts(monthValue);
      const response = await monthlyReportAPI.getGeneratedMonthlyReports({ month, year });
      setGeneratedReports(response.data.reports || []);
    } catch (err) {
      console.error('Failed to load generated monthly reports:', err);
      setGeneratedReports([]);
    } finally {
      setGeneratedReportsLoading(false);
    }
  };

  const handleGenerateMonthlySummary = async () => {
    try {
      setMonthlySummaryLoading(true);
      setMonthlySummaryError('');
      const { month, year } = monthValueToParts(selectedMonth);
      await monthlyReportAPI.generateMonthlyReport({ month, year });
      await loadMonthlySummary(selectedMonth);
      await loadGeneratedReports(selectedMonth);
    } catch (err) {
      console.error('Failed to generate monthly report:', err);
      setMonthlySummaryError(err.response?.data?.message || 'Failed to generate monthly report.');
    } finally {
      setMonthlySummaryLoading(false);
    }
  };

  const handleRefreshMonthlySummary = async () => {
    await loadMonthlySummary(selectedMonth);
    await loadGeneratedReports(selectedMonth);
  };

  const handleGenerateMonthlyPMs = async () => {
    if (!selectedMonth) {
      setPmGenerationResult({ success: false, message: 'Select a month first.' });
      return;
    }
    try {
      setPmGenerating(true);
      setPmGenerationResult(null);
      const { month, year } = monthValueToParts(selectedMonth);
      const response = await qdfAPI.generateMonthlyPMs({ month, year });
      setPmGenerationResult({
        success: true,
        createdCount: response.data.createdCount,
        skippedCount: response.data.skippedCount,
        month,
        year,
      });
    } catch (err) {
      console.error('Failed to generate monthly PMs:', err);
      setPmGenerationResult({
        success: false,
        message: err.response?.data?.message || 'Failed to generate monthly PMs.',
      });
    } finally {
      setPmGenerating(false);
    }
  };

  const openMonthlySummaryModal = async () => {
    setIsMonthlySummaryModalOpen(true);
    await loadMonthlySummary(selectedMonth);
    await loadGeneratedReports(selectedMonth);
  };

  useEffect(() => {
    if (isMonthlySummaryModalOpen && selectedMonth) {
      loadMonthlySummary(selectedMonth);
      loadGeneratedReports(selectedMonth);
    }
  }, [selectedMonth, isMonthlySummaryModalOpen]);

  return {
    selectedMonth,
    setSelectedMonth,
    monthlySummary,
    monthlySummaryError,
    monthlySummaryLoading,
    generatedReports,
    generatedReportsLoading,
    isMonthlySummaryModalOpen,
    setIsMonthlySummaryModalOpen,
    loadMonthlySummary,
    loadGeneratedReports,
    handleGenerateMonthlySummary,
    handleRefreshMonthlySummary,
    handleGenerateMonthlyPMs,
    pmGenerating,
    pmGenerationResult,
    openMonthlySummaryModal
  };
};
